import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { startQrScanner, stopQrScanner } from "@/lib/qrScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScanLine, Camera, CameraOff, LogIn, LogOut, AlertTriangle, Loader2, Maximize } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getDevicePosition } from "@/lib/geo";
import { JournalTab, EarlyExitsTab, GateSettingsTab } from "./PortailExtras";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const ResultCard = ({ result }) => {
  if (!result) return null;
  if (result.staff) {
    const isEntry = result.action === "entree";
    return (
      <div className={`rounded-2xl border-2 p-5 ${isEntry ? "bg-blue-50 border-blue-400" : "bg-slate-50 border-slate-400"}`} data-testid="scan-result-staff">
        <div className={`flex items-center gap-2 font-extrabold text-xl ${isEntry ? "text-blue-700" : "text-slate-700"}`}>
          {isEntry ? <LogIn className="h-6 w-6" /> : <LogOut className="h-6 w-6" />}
          {isEntry ? "POINTAGE PERSONNEL — ENTRÉE" : "POINTAGE PERSONNEL — SORTIE"}
        </div>
        <div className="mt-2 text-lg font-bold text-slate-900">{result.employee.name}</div>
        <div className="text-sm text-slate-600">Badge personnel · {fmtTime(result.time)}</div>
      </div>
    );
  }
  const { movement } = result;
  const isEntry = movement.type === "entree";
  const alert = movement.flag === "sortie_anticipee_non_autorisee";
  const authorized = movement.flag === "sortie_anticipee_autorisee";
  const bg = alert ? "bg-red-50 border-red-400" : isEntry ? "bg-green-50 border-green-400" : "bg-orange-50 border-orange-400";
  const color = alert ? "text-red-700" : isEntry ? "text-green-700" : "text-orange-700";
  return (
    <div className={`rounded-2xl border-2 p-5 ${bg}`} data-testid="scan-result">
      <div className={`flex items-center gap-2 font-extrabold text-xl ${color}`}>
        {alert ? <AlertTriangle className="h-6 w-6" /> : isEntry ? <LogIn className="h-6 w-6" /> : <LogOut className="h-6 w-6" />}
        {alert ? "SORTIE NON AUTORISÉE !" : isEntry ? "ENTRÉE ENREGISTRÉE" : "SORTIE ENREGISTRÉE"}
      </div>
      <div className="mt-2 text-lg font-bold text-slate-900">{movement.student_name}</div>
      <div className="text-sm text-slate-600">{movement.class_name} · {movement.matricule} · {fmtTime(movement.timestamp)}</div>
      {authorized && <div className="mt-1 text-xs font-semibold text-green-700">Sortie anticipée autorisée ✓</div>}
      {alert && <div className="mt-1 text-xs font-semibold text-red-700">Aucune autorisation de sortie anticipée pour aujourd&apos;hui. Le parent est alerté.</div>}
    </div>
  );
};

const ScannerTab = ({ onScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [direction, setDirection] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const qrRef = useRef(null);
  const coolRef = useRef(0);
  const dirRef = useRef("auto");
  dirRef.current = direction;

  const doScan = async (code) => {
    if (busy) return;
    setBusy(true);
    try {
      const gps = await getDevicePosition();
      const { data } = await api.post("/gate/scan", { code, direction: dirRef.current, ...(gps || {}) });
      setResult(data);
      if (data.staff) toast.success(data.action === "entree" ? `Pointage entrée : ${data.employee.name} ✓` : `Pointage sortie : ${data.employee.name} ✓`);
      else if (data.alert) toast.error("Sortie anticipée NON AUTORISÉE !");
      else toast.success(data.movement.type === "entree" ? "Entrée enregistrée ✓" : "Sortie enregistrée ✓");
      onScanned?.();
    } catch (e) {
      setResult(null);
      toast.error(e.response?.data?.detail || "Erreur de scan");
    } finally { setBusy(false); }
  };

  const startCamera = async () => {
    const onDecoded = (text) => {
      const now = Date.now();
      if (now - coolRef.current < 3000) return; // anti-doublon 3s
      coolRef.current = now;
      doScan(text);
    };
    try {
      qrRef.current = await startQrScanner("qr-reader", onDecoded);
      setScanning(true);
    } catch (e) {
      toast.error(e.message, { duration: 8000 });
    }
  };

  const stopCamera = async () => {
    await stopQrScanner(qrRef.current);
    setScanning(false);
  };

  useEffect(() => () => { stopQrScanner(qrRef.current); }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="scanner-tab">
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2"><Camera className="h-4 w-4" />Caméra QR</div>
            {scanning ? (
              <Button onClick={stopCamera} size="sm" variant="outline" className="gap-1 text-red-600" data-testid="btn-stop-camera"><CameraOff className="h-3.5 w-3.5" />Arrêter</Button>
            ) : (
              <Button onClick={startCamera} size="sm" className="bg-[#1E3A8A] text-white gap-1" data-testid="btn-start-camera"><Camera className="h-3.5 w-3.5" />Activer la caméra</Button>
            )}
          </div>
          <div className={`rounded-xl overflow-hidden ${scanning ? "" : "bg-slate-900 min-h-[220px]"}`}>
            {!scanning && <div className="text-slate-500 text-sm text-center py-16"><ScanLine className="h-8 w-8 mx-auto mb-2 opacity-40" />Caméra inactive — activez-la pour scanner les cartes QR</div>}
            <div id="qr-reader" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="font-heading font-bold text-[#1E3A8A] mb-3">Saisie manuelle (matricule élève ou code badge)</div>
          <div className="flex flex-wrap gap-2">
            <Input value={manual} onChange={e => setManual(e.target.value)} placeholder="MZD-26-00001 ou MAZID-STAFF-…"
              onKeyDown={e => { if (e.key === "Enter" && manual.trim()) { doScan(manual); setManual(""); } }}
              className="flex-1 min-w-[160px] font-mono" data-testid="manual-code-input" />
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-36" data-testid="direction-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="entree">Entrée</SelectItem>
                <SelectItem value="sortie">Sortie</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => { if (manual.trim()) { doScan(manual); setManual(""); } }} disabled={busy || !manual.trim()}
              className="bg-[#166534] text-white gap-1" data-testid="manual-scan-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ScanLine className="h-4 w-4" />Pointer</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Mode « Auto » : 1er scan du jour = entrée, scan suivant = sortie.</p>
        </div>
      </div>

      <div className="space-y-4">
        <ResultCard result={result} />
        {!result && (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
            Le résultat du scan s&apos;affichera ici.
          </div>
        )}
      </div>
    </div>
  );
};

export default function PortailScanPage() {
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const loadSummary = async () => {
    try { const { data } = await api.get("/gate/summary"); setSummary(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadSummary(); }, [refreshKey]);

  const cards = summary ? [
    ["Entrés aujourd'hui", summary.entres, "text-[#1E3A8A]"],
    ["Présents actuellement", summary.presents, "text-green-700"],
    ["Sortis", summary.sortis, "text-orange-600"],
    ["Alertes sorties", summary.alertes, "text-red-600"],
  ] : [];

  return (
    <div className="space-y-6" data-testid="portail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="chip mb-2"><ScanLine className="h-3 w-3" /><span>PORTAIL ENTRÉES / SORTIES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Scan cartes scolaires &amp; badges personnel</h1>
          <p className="text-slate-500 mt-1">Scannez le QR de la carte élève ou du badge du personnel (ou saisissez le matricule / code badge). Les parents sont notifiés automatiquement.</p>
        </div>
        <Button onClick={() => navigate("/kiosque-portail")} className="bg-[#0F172A] hover:bg-slate-800 text-white gap-2" data-testid="kiosque-mode-btn">
          <Maximize className="h-4 w-4" /> Mode kiosque (tablette)
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-3 text-center" data-testid={`summary-${label}`}>
            <div className={`text-2xl font-extrabold tabular-nums ${cls}`}>{val}</div>
            <div className="text-[11px] uppercase text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="scanner">
        <TabsList data-testid="portail-tabs">
          <TabsTrigger value="scanner" data-testid="tab-scanner">Scanner</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="sorties" data-testid="tab-sorties">Sorties anticipées</TabsTrigger>
          <TabsTrigger value="parametres" data-testid="tab-parametres">Paramètres</TabsTrigger>
        </TabsList>
        <TabsContent value="scanner" className="mt-4">
          <ScannerTab onScanned={() => setRefreshKey(k => k + 1)} />
        </TabsContent>
        <TabsContent value="journal" className="mt-4"><JournalTab key={refreshKey} /></TabsContent>
        <TabsContent value="sorties" className="mt-4"><EarlyExitsTab /></TabsContent>
        <TabsContent value="parametres" className="mt-4"><GateSettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
